import { useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const NODES = {
  paris: { x: 160, y: 210, hub: true },
  istanbul: { x: 350, y: 255 },
  tashkent: { x: 590, y: 185 },
  dubai: { x: 520, y: 345 },
  singapore: { x: 855, y: 415 },
};

const LINKS = [
  { from: "paris", to: "istanbul", cat: "industrial" },
  { from: "paris", to: "dubai", cat: "commodities" },
  { from: "paris", to: "tashkent", cat: "agriculture" },
  { from: "paris", to: "singapore", cat: "technology" },
  { from: "dubai", to: "singapore", cat: "commodities" },
];

const curve = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - 70;
  return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
};

export default function Corridors({ t }) {
  const [filter, setFilter] = useState("all");
  const active = (cat) => filter === "all" || filter === cat;

  return (
    <section
      id="corridors"
      data-testid="corridors-section"
      className="relative overflow-hidden py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-amberburn"
        >
          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
          {t.corridors.eyebrow}
        </motion.p>

        <div className="mb-10 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="max-w-xl font-serif text-2xl font-normal leading-tight text-forest sm:text-3xl lg:text-4xl"
          >
            {t.corridors.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="max-w-md text-sm leading-relaxed text-sand sm:text-base"
          >
            {t.corridors.sub}
          </motion.p>
        </div>

        <div className="mb-8 flex flex-wrap gap-2" data-testid="corridor-filters">
          {t.corridors.filters.map((f) => (
            <button
              key={f.id}
              data-testid={`corridor-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${
                filter === f.id
                  ? "border-forest bg-forest text-cream"
                  : "border-creamborder bg-creamdark/50 text-forest/60 hover:border-gold hover:text-forest"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
          className="relative overflow-hidden rounded-sm border border-creamborder bg-creamdark/40 p-2 sm:p-4"
          data-testid="corridor-map"
        >
          <svg
            viewBox="0 0 1000 560"
            className="h-auto w-full"
            role="img"
            aria-label="Trade corridor network"
          >
            <defs>
              <pattern id="dots" width="26" height="26" patternUnits="userSpaceOnUse">
                <circle cx="1.4" cy="1.4" r="1.4" fill="#0D2818" opacity="0.08" />
              </pattern>
            </defs>
            <rect width="1000" height="560" fill="url(#dots)" />

            {LINKS.map((l, i) => {
              const a = NODES[l.from];
              const b = NODES[l.to];
              const on = active(l.cat);
              return (
                <motion.path
                  key={i}
                  d={curve(a, b)}
                  fill="none"
                  stroke={on ? "#C59B27" : "#8C8370"}
                  strokeWidth={on ? 1.8 : 1}
                  strokeDasharray="5 6"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: on ? 0.95 : 0.18 }}
                  viewport={{ once: true }}
                  transition={{
                    pathLength: { duration: 1.6, delay: 0.3 + i * 0.2, ease: "easeInOut" },
                    opacity: { duration: 0.4 },
                  }}
                  data-testid={`corridor-link-${l.from}-${l.to}`}
                />
              );
            })}

            {Object.entries(NODES).map(([id, n], i) => {
              const on =
                filter === "all" ||
                n.hub ||
                LINKS.some(
                  (l) => l.cat === filter && (l.from === id || l.to === id)
                );
              return (
                <motion.g
                  key={id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: on ? 1 : 0.25 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.12 }}
                  data-testid={`corridor-node-${id}`}
                >
                  <circle cx={n.x} cy={n.y} r={n.hub ? 7 : 5} fill="#0D2818" />
                  <circle
                    cx={n.x}
                    cy={n.y}
                    r={n.hub ? 7 : 5}
                    fill="none"
                    stroke="#C59B27"
                    strokeWidth="1.4"
                    className="node-pulse"
                  />
                  <text
                    x={n.x}
                    y={n.y - (n.hub ? 20 : 16)}
                    textAnchor="middle"
                    className="fill-forest"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: n.hub ? "13px" : "11.5px",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    {t.corridors.nodeLabels[id]}
                  </text>
                </motion.g>
              );
            })}
          </svg>
          <p className="px-2 pb-2 pt-1 text-right font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
            {t.corridors.hint}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
